import React, {ReactElement, ReactNode} from "react";

import Icon from "../../icon/Icon";
import SearchPaginationLink from "../pagination-link/SearchPaginationLink";

import ArrowIcon from "/src/svg/arrow.svg";

import {css} from "/src/utils/utils";

import * as styles from "./search-pagination.module.scss";
import translate from "/src/utils/translate";

interface SearchPaginationProps {
  isFirstPage: boolean;
  currentPageNumber: number;
  isLastPage: boolean;
  pageCount: number;
  darkMode?: boolean;
  classes?: string;
  handlePage: (num: number) => void;
}

const createSearchPaginationLink = (
  pageNumber: number,
  showLink: boolean,
  darkMode: boolean,
  handlePage: (num: number) => void
): ReactNode => {
  const searchPaginationLinkOnClick = () => {
    handlePage(pageNumber);
  };

  return (
    <SearchPaginationLink
      showLink={showLink}
      page={pageNumber}
      darkMode={darkMode}
      onClick={searchPaginationLinkOnClick}
    />
  );
};

const SearchPagination = (props: SearchPaginationProps): ReactElement => {
  const {
    isFirstPage,
    currentPageNumber,
    isLastPage,
    pageCount,
    handlePage,
    darkMode,
    classes,
  } = props;

  return (
    <nav
      aria-label={translate("pagination")}
      className={css(
        styles.searchPagination,
        darkMode === true ? styles.searchPaginationDark : "",
        classes!
      )}
    >
      <ul className={styles.searchPagination__items}>
        {!isFirstPage && (
          <li className={styles.searchPagination__item}>
            <button
              tabIndex={0}
              onClick={() => {
                handlePage(currentPageNumber - 1);
              }}
              className={css(styles.searchPagination__item__iconBtn)}
              aria-label={translate("Previous Page")}
            >
              <Icon
                svg={ArrowIcon}
                role="presentation"
                classes={styles.searchPagination__icon}
              />
            </button>
          </li>
        )}

        <li
          className={css(
            styles.searchPagination__item,
            currentPageNumber - 4 > 0 ? "" : styles.searchPagination__itemHidden
          )}
        >
          {createSearchPaginationLink(
            1,
            currentPageNumber - 4 > 0,
            darkMode === true,
            handlePage
          )}
        </li>

        {currentPageNumber - 4 > 0 && (
          <li
            className={css(
              styles.searchPagination__item,
              styles.searchPagination__itemEllipsis
            )}
          >
            ...
          </li>
        )}

        <li
          className={css(
            styles.searchPagination__item,
            currentPageNumber - 3 > 0 ? "" : styles.searchPagination__itemHidden
          )}
        >
          {createSearchPaginationLink(
            currentPageNumber - 3,
            currentPageNumber - 3 > 0,
            darkMode === true,
            handlePage
          )}
        </li>

        <li
          className={css(
            styles.searchPagination__item,
            currentPageNumber - 2 > 0 ? "" : styles.searchPagination__itemHidden
          )}
        >
          {createSearchPaginationLink(
            currentPageNumber - 2,
            currentPageNumber - 2 > 0,
            darkMode === true,
            handlePage
          )}
        </li>

        <li
          className={css(
            styles.searchPagination__item,
            currentPageNumber - 1 > 0 ? "" : styles.searchPagination__itemHidden
          )}
        >
          {createSearchPaginationLink(
            currentPageNumber - 1,
            currentPageNumber - 1 > 0,
            darkMode === true,
            handlePage
          )}
        </li>

        <li
          className={css(
            "h4",
            styles.searchPagination__item,
            styles.searchPagination__itemCurrent
          )}
        >
          <span className={styles.searchPagination__visuallyHidden}>
            {translate("current page")}
          </span>{" "}
          {currentPageNumber}
        </li>

        <li
          className={css(
            styles.searchPagination__item,
            currentPageNumber + 1 <= pageCount
              ? ""
              : styles.searchPagination__itemHidden
          )}
        >
          {createSearchPaginationLink(
            currentPageNumber + 1,
            currentPageNumber + 1 <= pageCount,
            darkMode === true,
            handlePage
          )}
        </li>

        <li
          className={css(
            styles.searchPagination__item,
            currentPageNumber + 2 <= pageCount
              ? ""
              : styles.searchPagination__itemHidden
          )}
        >
          {createSearchPaginationLink(
            currentPageNumber + 2,
            currentPageNumber + 2 <= pageCount,
            darkMode === true,
            handlePage
          )}
        </li>

        <li
          className={css(
            styles.searchPagination__item,
            currentPageNumber + 3 <= pageCount
              ? ""
              : styles.searchPagination__itemHidden
          )}
        >
          {createSearchPaginationLink(
            currentPageNumber + 3,
            currentPageNumber + 3 <= pageCount,
            darkMode === true,
            handlePage
          )}
        </li>

        {currentPageNumber + 4 <= pageCount && (
          <li
            className={css(
              "h4",
              styles.searchPagination__item,
              styles.searchPagination__itemEllipsis
            )}
          >
            ...
          </li>
        )}

        <li
          className={css(
            styles.searchPagination__item,
            currentPageNumber + 4 <= pageCount
              ? ""
              : styles.searchPagination__itemHidden
          )}
        >
          {createSearchPaginationLink(
            pageCount,
            currentPageNumber + 4 <= pageCount,
            darkMode === true,
            handlePage
          )}
        </li>

        {!isLastPage && (
          <li className={styles.searchPagination__item}>
            <button
              tabIndex={0}
              onClick={() => {
                handlePage(currentPageNumber + 1);
              }}
              className={css(
                styles.searchPagination__item__iconBtn,
                styles.searchPagination__item__iconBtnNext
              )}
              aria-label={translate("Next Page")}
            >
              <Icon
                svg={ArrowIcon}
                role="presentation"
                classes={css(styles.searchPagination__icon)}
              />
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
};

export default SearchPagination;
